/* global _, define, nfRadio */
/* eslint-disable no-var, object-shorthand */
/**
 * What has been answered, and whether it passes.
 *
 * Three questions are asked of a step often enough to be worth asking in one
 * place: has anything been entered on it, may it be left, and is there
 * anything anywhere in the flow that leaving the page would discard. Each is
 * answered from the field models *and* the live controls together, because
 * Ninja Forms only writes a typed value into its model on change, which for a
 * text control means on blur — a visitor with the caret still in the field has
 * answered it without the model knowing.
 *
 * Validation itself is never performed here: each field is marked dirty and
 * sent through Ninja Forms' own `validate:field` request, so the rules, the
 * messages, and the error rendering stay the form's.
 *
 * Like the step and theme modules, this holds no state and reaches into no
 * view: it is given the step, the element that step is rendered as, and the
 * view's scoped lookup when a failing field has to be found.
 */
define( 'controllers/answers',[ 'models/steps' ], function ( cfSteps ) {
	/**
	 * What a select holds, and what it was rendered holding.
	 *
	 * A select has no `defaultValue`: the value it rendered with is the option
	 * carrying `selected`, or — when the markup names none — the first option,
	 * which the browser selects on the visitor's behalf and which is therefore
	 * just as much "what they were shown" as an explicit default.
	 *
	 * @param {Object}  control  A select control.
	 * @param {boolean} rendered True for the opening value, false for now.
	 * @return {string} The selected values, joined.
	 */
	function selectValue( control, rendered ) {
		var chosen = _.filter( control.options, function ( option ) {
			return rendered ? option.defaultSelected : option.selected;
		} );

		if ( rendered && ! chosen.length && ! control.multiple ) {
			chosen = control.options.length ? [ control.options[ 0 ] ] : [];
		}

		return _.map( chosen, function ( option ) {
			return String( option.value || '' ).trim();
		} ).join( '' );
	}

	/**
	 * Whether a control holds something the visitor entered into it.
	 *
	 * Not "does it hold anything": an owner's Default Value is rendered into
	 * the control before the visitor arrives, so a control still showing what
	 * it rendered with has not been answered — the same rule the field models
	 * are read by in models/steps, applied to the live control. Clearing a
	 * pre-filled control counts as nothing entered rather than as an answer,
	 * because there is no wording of the visitor's to lose.
	 *
	 * @param {Object} control A rendered control.
	 * @return {boolean} True when the visitor put the value there.
	 */
	function isVisitorEntry( control ) {
		if ( 'checkbox' === control.type || 'radio' === control.type ) {
			return control.checked && ! control.defaultChecked;
		}

		var isSelect = 0 === String( control.type ).indexOf( 'select' );
		var current = isSelect
			? selectValue( control, false )
			: String( control.value || '' ).trim();
		var rendered = isSelect
			? selectValue( control, true )
			: String( control.defaultValue || '' ).trim();

		return '' !== current && current !== rendered;
	}

	/**
	 * Report whether a step's rendered controls hold the visitor's own value.
	 *
	 * The element is passed in rather than found by the active-step class,
	 * because that class is only applied to non-multi-part steps — reading it
	 * directly would silently answer "nothing typed" for every multi-part step.
	 *
	 * @param {Object} active The step's element.
	 * @return {boolean} Whether anything has been entered on this step.
	 */
	function hasTypedValue( active ) {
		var controls = active.find(
			'input:not([type="hidden"]):not([type="submit"]):not([type="button"]), textarea, select'
		);

		return _.some( controls, isVisitorEntry );
	}

	/**
	 * Whether any of a step's fields holds a value the visitor entered.
	 *
	 * The question the models are asked is "is this value the visitor's",
	 * which models/steps answers from the field type and the value the field
	 * opened with together: a statement's markup and an owner's Default Value
	 * both live in the same `value` attribute a text field's typing does, so
	 * reading the attribute alone hands the form's own words back as the
	 * visitor's. Both of those are the ordinary case rather than an edge one —
	 * every seeded form opens with a statement, and a Default Value takes
	 * merge tags, which is how forms arrive pre-filled.
	 *
	 * @param {Object} step A step.
	 * @return {boolean} True when a visitor value has reached the field models.
	 */
	function hasStoredValue( step ) {
		return _.some( step.fields, function ( field ) {
			return cfSteps.hasVisitorValue( field );
		} );
	}

	/**
	 * Whether a step has been answered at all.
	 *
	 * @param {Object} step   The step.
	 * @param {Object} active The step's element.
	 * @return {boolean} True when the step holds an answer.
	 */
	function isAnswered( step, active ) {
		return hasTypedValue( active ) || hasStoredValue( step );
	}

	/**
	 * Whether a step must be answered before it may be left.
	 *
	 * @param {Object} step The step.
	 * @return {boolean} True when any of its fields is required.
	 */
	function isRequired( step ) {
		return _.some( step.fields, function ( field ) {
			return 1 === Number( field.get( 'required' ) );
		} );
	}

	/**
	 * Whether the visitor has entered something that leaving would lose.
	 *
	 * An unstarted flow is never guarded — there is nothing to discard
	 * before the first question, and a confirmation there would be a
	 * dialog in front of an exit the visitor has every right to take.
	 *
	 * @param {Array}   steps   Every step in the flow.
	 * @param {Object}  active  The current step's element.
	 * @param {boolean} started Whether the flow has started.
	 * @return {boolean} True when answers exist that were never sent.
	 */
	function hasUnsentAnswers( steps, active, started ) {
		if ( ! started ) {
			return false;
		}

		if ( hasTypedValue( active ) ) {
			return true;
		}

		return _.some( steps, hasStoredValue );
	}

	/**
	 * Validate a step through Ninja Forms' own validation.
	 *
	 * Each field is marked dirty and sent through the `validate:field`
	 * request, so the rules, messages, and error rendering are the
	 * form's, not this add-on's. Errors are read back off the models
	 * afterwards, and the first failing field is focused so the visitor
	 * is taken to the problem rather than told about it in the abstract.
	 *
	 * @param {Object}   step   The step to validate.
	 * @param {Function} lookup The view's scoped element lookup.
	 * @return {boolean} True when the step may be left.
	 */
	function validateStep( step, lookup ) {
		_.each( step.fields, function ( field ) {
			field.set( 'clean', false );
			nfRadio.channel( 'submit' ).trigger( 'validate:field', field );
		} );

		var invalid = _.find( step.fields, function ( field ) {
			return field.get( 'errors' ) && field.get( 'errors' ).length;
		} );

		if ( invalid ) {
			var input = lookup( '#nf-field-' + invalid.get( 'id' ) ).get( 0 );
			if ( input ) {
				input.focus();
			}
			return false;
		}

		return true;
	}

	return {
		hasTypedValue: hasTypedValue,
		hasStoredValue: hasStoredValue,
		isAnswered: isAnswered,
		isRequired: isRequired,
		hasUnsentAnswers: hasUnsentAnswers,
		validateStep: validateStep,
	};
} );

